import { ScaleAliasPrefix } from '../enums/ScaleAlias';
import { generateScaleAliases } from '../utils/scaleAliases';
/**
 * Based of the Design System update research made by the Frontend Team
 * https://home24.atlassian.net/wiki/spaces/TT/pages/222167527/Design+system+update+research
 * We decided to start a process of updating our design system using `Scale Aliases`
 * a feature of the `styled-system` library.
 *
 * Based on this decision, initially, any new values ​​that need to be added to the style properties
 * `space`, `fontSizes`, and `lineHeights` must be added to the `extendedProps` property below.
 */
var extendedProps = {
    space: [2, 12, 20, 40],
    fontSizes: [20, 28],
    lineHeights: ['14px', '22px', '28px'],
};
var minWidth = '320px';
var breakpoints = ['632px', '984px', '1392px'];
var maxWidth = '1600px';
/**
 * Scale Aliases are generated for the following style properties below:
 * `space`, `fontSizes` and `lineHeights`.
 */
var space = generateScaleAliases(ScaleAliasPrefix.Space, [0, 4, 8, 16, 24, 32, 48, 64, 96, 128, 192, 256, 320], extendedProps.space);
var fontSizes = generateScaleAliases(ScaleAliasPrefix.FontSizes, [11, 12, 13, 14, 16, 18, 24, 32, 40, 48], extendedProps.fontSizes);
var lineHeights = generateScaleAliases(ScaleAliasPrefix.LineHeights, [1, '16px', '20px', '24px', '32px', '40px', '48px', '56px', '64px'], extendedProps.lineHeights);
var fonts = {
    'proxima-nova': 'proxima-nova, Arial, Helvetica, sans-serif',
    fallback: 'Arial, Helvetica, sans-serif',
};
var colors = {
    brand: {
        h24: {
            lighter: '#f5674c',
            normal: '#f45334',
            darker: '#f33f1c',
        },
        ffh: {
            lighter: '#5a9db2',
            normal: '#4d90a6',
            darker: '#458195',
        },
    },
    gray: [
        '#ffffff',
        '#fafafa',
        '#f7f7f7',
        '#f2f2f2',
        '#e2e4e4',
        '#bec4c4',
        '#b1b7b7',
        '#a9a9a9',
        '#8e9597',
        '#565d60',
        '#2c3638',
        '#323232',
        '#e7ebee',
        '#ededed',
        '#f0f0f0',
        '#9a9ea0',
        '#1e2122',
    ],
    orange: {
        30: '#f79776',
        50: '#f6755c',
        60: '#f45334',
        70: '#b73e27',
    },
    green: {
        10: '#cceee1',
        60: '#02a867',
        80: '#027e4d',
    },
    yellow: {
        20: '#fef5cc',
        90: '#866f01',
    },
    red: {
        10: '#f8d4d3',
        60: '#de2723',
        80: '#a71d1a',
    },
    bluePetrol: {
        40: '#71bcd1',
    },
    indigoBlue: {
        20: '#d0dbf3',
        80: '#0b2661',
    },
    purple: {
        40: '#a184be',
    },
    pink: [
        '#fcd5cf',
    ],
    white: '#ffffff',
    text: '#2c3638',
    sale: '#db2916',
    'light-overlay': 'rgba(0, 0, 0, 0.1)',
    overlay: 'rgba(0, 0, 0, 0.25)',
    'darker-overlay': 'rgba(44, 54, 56, 0.6)',
    alert: {
        notice: '#badff2',
        success: '#c0dec2',
        error: '#ff9f9f',
        warning: '#ffed9f',
    },
    'white-overlay': 'rgba(255, 255, 255, 0.7);',
    'chateau-green': '#429955',
    ratingStar: {
        full: '#face01',
        empty: '#e3e3e3',
    },
    delivery: {
        fast: '#02A867',
    },
};
var borders = [
    '1px solid #e2e4e4',
    '1px solid #bec4c4',
    '1px solid #565d60',
    '1px solid #d8d8d8',
    '1px solid #f45334',
    '1px solid #787d80',
];
var boxShadows = [
    'inset 0 0 0 1px #565d60',
    'inset 0 -1px 0 #e2e4e4',
    '0 1px 15px 0 rgba(13, 27, 34, 0.2)',
];
var shadows = {
    top: 'inset 0 1px 0 #e2e4e4',
    bottom: 'inset 0 -1px 0 #e2e4e4',
    left: 'inset 1px 0 0 #e2e4e4',
    right: 'inset -1px 0 0 #e2e4e4',
};
var radii = {
    br_3: '3px',
};
export var Theme;
(function (Theme) {
    Theme["h24"] = "h24";
    Theme["ffh"] = "ffh";
})(Theme || (Theme = {}));
export default {
    breakpoints: breakpoints,
    space: space,
    colors: colors,
    borders: borders,
    boxShadows: boxShadows,
    fonts: fonts,
    fontSizes: fontSizes,
    minWidth: minWidth,
    minWidths: [minWidth],
    maxWidth: maxWidth,
    maxWidths: [maxWidth],
    lineHeights: lineHeights,
    shadows: shadows,
    radii: radii,
};
